import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
    IconButton, Tooltip, TextField, MenuItem, Grid, Button, InputAdornment, Pagination, Stack
} from '@mui/material';
import { Visibility, Search, Add, Refresh } from '@mui/icons-material';
import * as actionTypes from '../../redux/actionTypes';

const STATUS_COLORS = { Created: '#2196F3', Review: '#FF9800', Working: '#9C27B0', Closed: '#4CAF50' };
const TYPE_COLORS = { Bug: '#f44336', 'Feature Request': '#2196F3', Enhancement: '#4CAF50', Crash: '#FF5722', 'UI Problem': '#FF9800' };
const STATUSES = ['', 'Created', 'Review', 'Working', 'Closed'];
const TYPES = ['', 'Bug', 'Feature Request', 'Enhancement', 'Crash', 'UI Problem'];
const MODULES = ['', 'Recharge', 'Flight', 'Hotel', 'Kundli', 'Milan Sathi', 'Yatra', 'Sanchar', 'Wallet', 'Auth', 'Home', 'Astrologer', 'E-Commerce', 'Admin Panel', 'Backend', 'Other'];

const IssueList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const issues = useSelector(state => state.issueReducer?.issues);
    const pagination = useSelector(state => state.issueReducer?.pagination);

    const [filters, setFilters] = useState({
        status: searchParams.get('status') || '',
        type: '',
        module: '',
        search: '',
        page: 1,
        limit: 20,
    });

    const fetchIssues = useCallback(() => {
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.type) params.type = filters.type;
        if (filters.module) params.module = filters.module;
        if (filters.search) params.search = filters.search;
        params.page = filters.page;
        params.limit = filters.limit;
        dispatch({ type: actionTypes.GET_ISSUES, payload: params });
    }, [dispatch, filters]);

    useEffect(() => { fetchIssues(); }, [fetchIssues]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    const handleReset = () => {
        setFilters({ status: '', type: '', module: '', search: '', page: 1, limit: 20 });
    };

    const getStatusChip = (status) => (
        <Chip label={status} size="small" sx={{ bgcolor: STATUS_COLORS[status] || '#999', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
    );
    const getTypeChip = (type) => (
        <Chip label={type} size="small" variant="outlined" sx={{ borderColor: TYPE_COLORS[type] || '#999', color: TYPE_COLORS[type] || '#999', fontWeight: 500, fontSize: '0.7rem' }} />
    );

    const getPlatformChips = (platforms) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {platforms?.map((p, i) => <Chip key={i} label={p} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />)}
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e' }}>📋 All Issues</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/issues/create')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1976d2' }}>
                    Create Issue
                </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField fullWidth size="small" label="Search" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField fullWidth size="small" select label="Status" value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                            {STATUSES.map(s => <MenuItem key={s} value={s}>{s || 'All Status'}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField fullWidth size="small" select label="Type" value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
                            {TYPES.map(t => <MenuItem key={t} value={t}>{t || 'All Types'}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField fullWidth size="small" select label="Module" value={filters.module} onChange={(e) => handleFilterChange('module', e.target.value)}>
                            {MODULES.map(m => <MenuItem key={m} value={m}>{m || 'All Modules'}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3} md={1.5}>
                        <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={handleReset} sx={{ borderRadius: 2, textTransform: 'none', height: 40 }}>
                            Reset
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={1.5}>
                        <Chip label={`Total: ${pagination?.total || 0}`} color="primary" sx={{ fontWeight: 600 }} />
                    </Grid>
                </Grid>
            </Paper>

            {/* Table */}
            <Paper sx={{ borderRadius: 3 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Issue ID</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Module</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Platform</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {issues && issues.length > 0 ? issues.map((issue) => (
                                <TableRow key={issue._id} hover sx={{ '&:hover': { bgcolor: '#f5f8ff' } }}>
                                    <TableCell sx={{ fontWeight: 700, color: '#1976d2', cursor: 'pointer' }} onClick={() => navigate(`/issues/${issue._id}`)}>{issue.issueId}</TableCell>
                                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue.title}</TableCell>
                                    <TableCell>{getTypeChip(issue.type)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem' }}>{issue.module}</TableCell>
                                    <TableCell>{getPlatformChips(issue.platform)}</TableCell>
                                    <TableCell>{getStatusChip(issue.status)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{issue.createdBy}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{new Date(issue.createdAt).toLocaleDateString('en-IN')}</TableCell>
                                    <TableCell>
                                        <Tooltip title="View Details">
                                            <IconButton size="small" onClick={() => navigate(`/issues/${issue._id}`)} sx={{ color: '#1976d2' }}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5, color: '#999' }}>
                                    <Typography variant="h6">No issues found</Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>Try changing filters or create a new issue</Typography>
                                </TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                    <Stack direction="row" justifyContent="center" sx={{ py: 2 }}>
                        <Pagination count={pagination.totalPages} page={filters.page} onChange={(e, page) => setFilters(prev => ({ ...prev, page }))}
                            color="primary" shape="rounded" />
                    </Stack>
                )}
            </Paper>
        </Box>
    );
};

export default IssueList;
